import * as React from 'react';
import glamorous from 'glamorous';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import { convertStringNumberToNumber, formatNumber, GetSuggestedMax } from '../../helpers/ChartHelper';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { ChartCommonProps, IconButtonWrapper, ChartCommonState } from '../Common';
import { WithStyles, withStyles, IconButton } from '@material-ui/core';
import { styles } from '../styles';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { WithTranslation, withTranslation } from 'react-i18next';
import { TranslationTextList } from '../../locales/common';
import { HelpWrapper } from '../../home/Common';
import { ChartInfo } from '../../components/ChartInfo';
import { ChartFilter } from '../../home/ChartComponents';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../../home/chartTheme';
import { ChartHeading } from '../../home/ChartHeading';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../../home/ChartEmptyState';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

// tslint:disable-next-line:no-any
const CardWrapper = glamorous.div<{ theme?: any }>((props) => ({
    // padding: 15,
    display: 'flex',
    flexFlow: 'column',
    textAlign: 'center',
    // fontSize: '18px',
    height: '100%',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 0 14px 0 rgba(81,115,255,0.09)',
    width: '100%',
    '@media print': {
        alignItems: 'center',
    },
}));

const BarWrapper = glamorous.div<{ theme?: object }>((props) => ({
    position: 'relative',
    paddingBottom: '10px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    maxWidth: '100%',
    maxHeight: '100%',
    height: '100%',
}));

const BarTitleContainer = glamorous.div<{ theme?: object }>((props) => ({
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    padding: '15px 15px 0px 15px',
    fontSize: '16px',
    fontWeight: 600,
    [`& .${`h1`}`]: {},
    [`& .${`h2`}`]: {
        fontSize: '2.5rem',
        padding: '10px 0',
    },
    [`& .${`h3`}`]: {
        paddingTop: '10px',
    },
    '& .increase': {
        color: 'green',
    },
    '& .decrease': {
        color: 'red',
    },
})).withProps({ className: 'rv-chart-title-area' });

const BarContainer = glamorous.div<{ theme?: object }>((props) => ({
    padding: '5px 15px 15px 15px',
    minHeight: '300px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: '350px',
    maxWidth: 'auto',
    position: 'relative',
    '@media (max-width: 533px)': {
        maxWidth: '350px',
        margin: '0 auto',
    },
    [`& .${`titleWrapper`}`]: {
        textAlign: 'center',
        display: 'inline-block',
        width: '100%',
        padding: '15px',
        [`& .${`title`}`]: {
            fontSize: '18px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`value`}`]: {
            fontSize: '24px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`secondary-title`}`]: {
            fontSize: '16px',
            fontWeight: 400,
            display: 'block',
            marginBottom: '15px',
        },
    },
}));

interface ReferralsByLeadQualityData {
    groupedReferralsByLeadQuality: Array<{
        groupedBy: string;
        count: number;
    }>;
    referredEnquiriesCount: number;
}

interface ChartProps extends ChartCommonProps, WithStyles<typeof styles>, WithTranslation {
    getSelectedItem?: (searchCriterias: Array<SearchCriteria>, isShowCompleted: boolean, title?: string) => void;
    chartFilter?: ChartFilter;
    data?: ReferralsByLeadQualityData;
}

interface ChartState extends ChartCommonState {
    //
}

class ReferralsByLeadQualityChart extends React.Component<ChartProps, ChartState> {
    // tslint:disable-next-line: no-any
    public chartRef: React.RefObject<any>;
    private chartFilter: ChartFilter | undefined;

    constructor(props: ChartProps, state: ChartState) {
        super(props, state);
        this.onCloseChart = this.onCloseChart.bind(this);
        this.handlePopoverOpen = this.handlePopoverOpen.bind(this);
        this.handlePopoverClose = this.handlePopoverClose.bind(this);
        this.onHover = this.onHover.bind(this);

        this.onPan = this.onPan.bind(this);
        this.getStarRepresentation = this.getStarRepresentation.bind(this);

        this.chartRef = React.createRef();

        this.chartFilter = this.props.chartFilter;

        this.state = {
            open: false,
            anchorEl: undefined,
        };
    }

    private getStarRepresentation(value: number): string {
        // Convert numeric value to star representation (filled stars only)
        const rating = Math.round(value);
        if (rating === 0) {
            return 'No Stars';
        }
        return '★'.repeat(Math.min(rating, 5));
    }

    public componentDidMount() {
        //
    }

    public componentWillUnmount() {
        //
    }
    // tslint:disable-next-line: no-any
    public componentWillReceiveProps(nextProps: Readonly<ChartProps>, nextContext: any): void {
        if (this.props.chartFilter !== nextProps.chartFilter) {
            this.chartFilter = nextProps.chartFilter;
        }
    }

    public render() {
        const { t } = this.props as {
            t: (key: keyof TranslationTextList) => string;
        };

        // If data prop is provided, render directly without GraphQL query
        if (this.props.data) {
            return this.renderChart(this.props.data, t);
        }

        // Otherwise, fetch data using GraphQL query
        return (
            <CardWrapper>
                {/* tslint:disable-next-line: no-any */}
                <Query<any>
                    query={ChartData}
                    variables={{
                        entityType: this.chartFilter?.entityType,
                        entityGuid: this.chartFilter?.entityGuid,
                    }}
                >
                    {({ loading, error, data }) => {
                        if (loading) {
                            return (
                                <ChartSkeleton variant="bars" />
                            );
                        }

                        const statistics = data.statistics;

                        let lead = loading || error || statistics === null || statistics === undefined ? null : statistics.lead;

                        let referralsByLeadQuality = lead ? lead.referralsByLeadQuality : null;

                        // Handle empty data - still show chart with empty arrays
                        const referralData: ReferralsByLeadQualityData = referralsByLeadQuality || {
                            groupedReferralsByLeadQuality: [],
                            referredEnquiriesCount: 0
                        };

                        return this.renderChart(referralData, t);
                    }}
                </Query>
            </CardWrapper>
        );
    }

    private renderChart(referralData: ReferralsByLeadQualityData, t: (key: keyof TranslationTextList) => string) {
        const { groupedReferralsByLeadQuality, referredEnquiriesCount } = referralData;

        // tslint:disable-next-line:no-any
        let groupedBy = groupedReferralsByLeadQuality.map((value: any) => {
            return value.groupedBy;
        });

        let highestValue = 0;
        // tslint:disable-next-line: no-any
        let countData = groupedReferralsByLeadQuality.map((value: any) => {
            if (highestValue < value.count) {
                highestValue = value.count;
            }
            return value.count;
        });

        groupedBy = groupedBy.reverse();
        countData = countData.reverse();

        return (
            <CardWrapper>
                <BarWrapper className={RV_CHART_CLASS}>
                                {this.props.description && !this.props.onCloseChart && (
                                    <HelpWrapper>
                                        <ChartInfo
                                            description={this.props.description}
                                        />
                                    </HelpWrapper>
                                )}
                                {this.props.onCloseChart && (
                                    <IconButtonWrapper aria-label="close">
                                        {this.props.description && (
                                            <HelpWrapper className="rv-chart-help">
                                                <ChartInfo
                                                    description={this.props.description}
                                                />
                                            </HelpWrapper>
                                        )}
                                        <IconButton aria-label="close" onClick={this.onCloseChart} size="small">
                                            <CloseIcon />
                                        </IconButton>
                                    </IconButtonWrapper>
                                )}
                                <BarTitleContainer>
                                    <ChartHeading icon={<PeopleOutlinedIcon />} title="Referrals by Lead Quality" />
                                    <div className="h2">{formatNumber(referredEnquiriesCount)}</div>
                                    <div className="h3">Total Referred {t('Enquiries')}</div>
                                </BarTitleContainer>
                                <BarContainer className="rv-chart-bars">
                                        <Bar
                                            ref={this.chartRef}
                                            // tslint:disable-next-line: no-any
                                            onClick={(event: React.MouseEvent<any>) => {
                                                const _data = getElementAtEvent(this.chartRef.current, event);
                                                if (_data.length > 0 && this.props.getSelectedItem) {
                                                    // Add click handling logic here if needed
                                                }
                                            }}
                                            data={{
                                                labels: groupedBy,
                                                datasets: [
                                                    {
                                                        label: 'Referrals',
                                                        ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                                        data: countData,
                                                    },
                                                ],
                                            }}
                                            options={{
                                                onHover: this.onHover,
                                                indexAxis: 'y' as const,
                                                animation: rvChartAnimation(),
                                                elements: { bar: { borderWidth: 0 } },
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                scales: {
                                                    x: {
                                                        grid: rvValueAxisStyle.grid,
                                                        suggestedMax: GetSuggestedMax(highestValue),
                                                        beginAtZero: true,
                                                        ticks: {
                                                            ...rvValueAxisStyle.ticks,
                                                            autoSkip: false,
                                                            callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                                                return `${formatNumber(tickValue)}`;
                                                            },
                                                        },
                                                    },
                                                    y: {
                                                        grid: rvCategoryAxisStyle.grid,
                                                        ticks: {
                                                            ...rvCategoryAxisStyle.ticks,
                                                            autoSkip: false,
                                                            callback: (tickValue: string | number, index: number, ticks: Tick[]) => {
                                                                // Get the label for this tick (for categorical axis, tickValue is the index)
                                                                const label = groupedBy[typeof tickValue === 'number' ? tickValue : index];
                                                                
                                                                // Try to parse it as a number
                                                                const numValue = typeof label === 'string' ? parseFloat(label) : label;
                                                                
                                                                // If it's a valid number between 0-5, show stars
                                                                if (!isNaN(numValue) && numValue >= 0 && numValue <= 5) {
                                                                    return this.getStarRepresentation(numValue);
                                                                }
                                                                
                                                                // Otherwise, return the original label
                                                                return label;
                                                            },
                                                            font: {
                                                                size: 22,
                                                            },
                                                            color: '#FFD700'
                                                        }
                                                    }
                                                },
                                                plugins: {
                                                    legend: {
                                                        position: 'bottom' as const,
                                                        display: false,
                                                    },
                                                    title: {
                                                        display: false,
                                                        text: ``,
                                                    },
                                                    zoom: {
                                                        pan: {
                                                            enabled: false,
                                                            onPan: this.onPan,
                                                        },
                                                        zoom: {
                                                            wheel: {
                                                                enabled: false,
                                                            },
                                                        },
                                                    },
                                                    tooltip: {
                                                        ...rvTooltipStyle,
                                                        callbacks: {
                                                            // tslint:disable-next-line: no-any
                                                            title: (context: any) => {
                                                                // Get the Y-axis label (the rating value)
                                                                const label = context[0].label;
                                                                const numValue = typeof label === 'string' ? parseFloat(label) : label;
                                                                
                                                                // If it's a valid number, show star text
                                                                if (!isNaN(numValue) && numValue >= 0 && numValue <= 5) {
                                                                    const starText = numValue === 1 ? 'Star' : 'Stars';
                                                                    return `${numValue} ${starText}`;
                                                                }
                                                                
                                                                return label;
                                                            },
                                                            // tslint:disable-next-line: no-any
                                                            label: function (context: any) {
                                                                const {
                                                                    formattedValue,
                                                                    dataset,
                                                                } = context;

                                                                var currentValue = convertStringNumberToNumber(formattedValue);
                                                                var enquiries = currentValue !== 1 ? 'enquiries' : 'enquiry';

                                                                return `${dataset.label}: ${formatNumber(currentValue)} ${enquiries}`;
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {isChartDataEmpty(countData) && (
                                            <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                                        )}
                                </BarContainer>
                            </BarWrapper>
                        </CardWrapper>
        );
    }

    private onCloseChart() {
        if (this.props.onCloseChart) {
            return this.props.onCloseChart(`IsReferralsByLeadQualityChartOpen`);
        }
    }

    // tslint:disable-next-line: no-any
    private handlePopoverOpen(event: any) {
        this.setAnchorEl(event.currentTarget);
    }

    private handlePopoverClose = () => {
        this.setAnchorEl(null);
    }

    // tslint:disable-next-line: no-any
    private setAnchorEl(anchorElValue: any) {
        this.setState({
            anchorEl: anchorElValue,
            open: Boolean(anchorElValue),
        });
    }

    private onPan = (context: { chart: Chart }) => {
        this.chartRef.current.options.scales.x.min = 0;
        this.chartRef.current.update();
    }

    private onHover(event: ChartEvent, elements: ActiveElement[], chart: Chart) {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    }
}

// tslint:disable-next-line: no-any
export default withTranslation()(withStyles(styles as any, { withTheme: true })(ReferralsByLeadQualityChart as any) as any);

const ChartData = gql`
    query ReferralsByLeadQualityChartData($entityType: Int, $entityGuid: String) {
        statistics {
            lead {
                referralsByLeadQuality(entityType: $entityType, entityGuid: $entityGuid) {
                    groupedReferralsByLeadQuality {
                      groupedBy
                      count
                    }
                    referredEnquiriesCount
                }
            }
        }
    }
`;
